// HeroBanner — reusable illustrated banner for the top of a page.
//
// Why: the app's inner pages were image-less — walls of panels and
// tiles without any pictorial anchor. This component gives every
// landing page a tall, image-forward hero that sets the sport context
// visually before the data loads.
//
// Props:
//   • sport        — "Cricket" | "Volleyball" | "mixed" (default). Picks
//                     the illustration. "mixed" uses cricket on even
//                     pages / volleyball on odd via `altFlavor`.
//   • eyebrow      — tiny uppercase caption above the title
//   • title        — big display line (JSX allowed for italic emphasis)
//   • subtitle     — single-line body copy under the title
//   • rightSlot    — optional JSX to render on the right side of the
//                     banner (actions, stats, counters)
//   • altFlavor    — swap illustration side-to-side, for pages that
//                     want visual rhythm when stacked
//
// All SVGs are local assets (served from /public/images/), so no
// external image hosts. ~22KB total for both heroes vs. a photo CDN
// at 400KB+.

import React from "react";

const SPORT_SRC = {
  Cricket:    "/images/cricket-hero.svg",
  Volleyball: "/images/volleyball-hero.svg"
};

export default function HeroBanner({
  sport = "mixed",
  eyebrow,
  title,
  subtitle,
  rightSlot,
  altFlavor = false,
  className = "",
}) {
  // "mixed" picks whatever's up this render — flips with altFlavor so
  // stacked banners alternate instead of twinning.
  const actualSport = sport === "mixed"
    ? (altFlavor ? "Volleyball" : "Cricket")
    : sport;
  const src = SPORT_SRC[actualSport] || SPORT_SRC.Cricket;

  return (
    <section className={`heroBanner heroBanner--${actualSport.toLowerCase()}${altFlavor ? " heroBanner--alt" : ""} ${className}`}>
      <div className="heroBanner-art" aria-hidden="true">
        <img
          src={src}
          alt=""
          onError={e => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      <div className="heroBanner-body">
        {eyebrow && <span className="heroBanner-eyebrow">{eyebrow}</span>}
        {title && <h1 className="heroBanner-title">{title}</h1>}
        {subtitle && <p className="heroBanner-subtitle">{subtitle}</p>}
      </div>
      {rightSlot && (
        <div className="heroBanner-right">
          {rightSlot}
        </div>
      )}
    </section>
  );
}
